import { jwtDecode } from 'jwt-decode';
import type { KyInstance } from 'ky';
import ky, { HTTPError } from 'ky';
import { TempoApiError, tempoApiErrorSchema } from '@/lib/api/tempoApiTypes';
import { getBriefErrorMessage } from '@/lib/utils/common/getBriefErrorMessage';
import { getEnv } from '../utils/common/getEnv';

function createTokenManager() {
  let getTokenFn: (() => Promise<string | null>) | null = null;
  let cachedToken: string | null = null;
  let tokenExpirySeconds = 0;
  const clearToken = () => {
    cachedToken = null;
    tokenExpirySeconds = 0;
  };
  return {
    setTokenProvider(provider: () => Promise<string | null>) {
      getTokenFn = provider;
    },

    setToken(token?: string) {
      cachedToken = token ?? null;
    },

    async getToken(forceRefresh = false): Promise<string | null> {
      if (!getTokenFn) {
        return null;
      }
      const nowInSeconds = Math.floor(Date.now() / 1000);
      if (!forceRefresh && cachedToken && nowInSeconds < tokenExpirySeconds) {
        return cachedToken;
      }

      try {
        cachedToken = await getTokenFn();
        if (!cachedToken) {
          clearToken();
          return null;
        }
        const leeway = 2 * 60; // 2 minutes in seconds
        const decodedToken = jwtDecode<{ exp: number }>(cachedToken);
        tokenExpirySeconds = decodedToken.exp - leeway;
        return cachedToken;
      } catch (_error) {
        clearToken();
        return null;
      }
    },
    clearToken,
  };
}

const tokenManager = createTokenManager();

export const setTokenProvider = (provider: () => Promise<string | null>, token?: string) => {
  tokenManager.setTokenProvider(provider);
  tokenManager.setToken(token);
};

export const tempoApi = (orgId?: number, usePrefix = true): KyInstance => {
  const prefixUrl = getEnv().apiBaseUrl;
  return ky.create({
    prefixUrl: usePrefix ? prefixUrl : '',
    hooks: {
      beforeRequest: [
        async (request) => {
          const token = await tokenManager.getToken();
          if (token) {
            request.headers.set('Authorization', `Bearer ${token}`);
          }
          if (orgId) {
            request.headers.set('Organization-Id', orgId.toString());
          }
        },
      ],
      beforeError: [
        (error) => {
          const request =
            error instanceof HTTPError && !(error instanceof TempoApiError)
              ? error.request
              : undefined;
          const method = request?.method ?? 'UNKNOWN';
          const url = request?.url ?? 'UNKNOWN';
          const briefErrorMessage = getBriefErrorMessage(method, url);
          error.message = `${briefErrorMessage} - ${error.message}`;
          return error;
        },
      ],
      afterResponse: [
        async (request, _options, response) => {
          if (response.ok) return response;

          if (response.status === 401) {
            const newToken = await tokenManager.getToken(true);

            if (newToken) {
              const newRequest = request.clone();
              newRequest.headers.set('Authorization', `Bearer ${newToken}`);
              return ky(newRequest);
            }
          }

          if (response.status === 400 || response.status === 409) {
            let raw: unknown;
            try {
              raw = await response.clone().json();
            } catch {
              throw new Error('Non-JSON error response');
            }

            // Add status to the raw response object for error parsing
            const rawWithStatus = { ...(raw as Record<string, unknown>), status: response.status };

            // biome-ignore lint/style/noNonNullAssertion: Assuming the schema is correct
            const parsed = tempoApiErrorSchema.parse(rawWithStatus)!;

            throw new TempoApiError(parsed, response, request);
          }

          return response;
        },
      ],
    },
  });
};
